import { ErrorResponse } from '@apollo/client/link/error'
import { GraphQLError } from 'graphql'
import { message } from 'antd'
import { ERROR_MESSAGE_MAP, SERVER_MESSAGE_ERROR_MAP } from './errorMessages'

function getGqlErrors(error: ErrorResponse): GraphQLError[] {
  const result: GraphQLError[] = []

  if (error.graphQLErrors?.length) {
    result.push(...error.graphQLErrors)
  }

  return result
}

function isNetworkError(error: ErrorResponse) {
  return !!error.networkError && !error.graphQLErrors?.length
}

/**
 * OWN-property lookup, deliberately (#7700). The key is a server-controlled, free-form string, so a plain
 * index read would reach `Object.prototype`: a GraphQL error whose message is literally `constructor` /
 * `toString` / `hasOwnProperty` resolves to an inherited FUNCTION, which is truthy, short-circuits the
 * `??` below and survives the `!!msg` filter — then `errMessages.join('\n')` stringifies it, so the toast
 * renders `function Object() { [native code] }` instead of copy. Own-property only.
 */
function lookupByServerMessage(serverMessage: string): string | undefined {
  return Object.prototype.hasOwnProperty.call(SERVER_MESSAGE_ERROR_MAP, serverMessage)
    ? SERVER_MESSAGE_ERROR_MAP[serverMessage]
    : undefined
}

function extractErrorMessages(error: ErrorResponse): string[] {
  if (isNetworkError(error)) {
    return ['Network error. Please check your network or contact our support to help you fix it.']
  }

  // Exact server-message match first (#7700), then the code map. Unmatched on both stays unmapped and is
  // filtered out below — neither lookup is a catch-all.
  return getGqlErrors(error)
    .map(
      ({ message: serverMessage, extensions }) =>
        lookupByServerMessage(serverMessage) ?? ERROR_MESSAGE_MAP[extensions?.code as string],
    )
    .filter(msg => !!msg)
}

export function hasUnauthenticatedError(error: ErrorResponse) {
  const gqlErrors = getGqlErrors(error)
  return gqlErrors.some(
    err =>
      err.extensions?.code === 'UNAUTHENTICATED' ||
      err.message?.toLowerCase().includes('jwt expired') ||
      err.message?.toLowerCase().includes('jwt malformed'),
  )
}

export function handleCurvoError(error: ErrorResponse) {
  const errMessages = extractErrorMessages(error)
  if (errMessages?.length > 0) {
    message.error(errMessages.join('\n'))
  }
}
